import React from 'react'
import CampaignsIcon from '../../Assets/Images/icons/campaigns'
import {
  getCampaignsLink,
  getCampaignLink,
  getCityPlanLink,
} from '../../Utils/Link'
import {
  formatToLocalDate,
  dropTimeZoneFromDate,
  isLocalDateStr,
} from '../../shared/libraries/utils/date'
import { MISSION_STATUS_ENUM } from '../../shared/constants'
import { PATHS } from '../../Config/Routing/paths'

export const NON_STARTED_STATUSES = [
  MISSION_STATUS_ENUM.DRAFT,
  MISSION_STATUS_ENUM.AVAILABLE,
  MISSION_STATUS_ENUM.BOOKED,
]
const NON_EDITABLE_STATUSES = [
  MISSION_STATUS_ENUM.UNDER_REVIEW,
  MISSION_STATUS_ENUM.COMPLETED,
  MISSION_STATUS_ENUM.PAID,
  MISSION_STATUS_ENUM.CANCELLED,
]

/**
 * Generates data that is needed for initial overlays render and can be consumed by <DrawMap /> component
 * @param {array} missions Missions data
 * @param {boolean} options.isClient Indicates whether current user is a client
 * @param {clickable} options.clickable disable click event on google map polygon
 *
 * @returns {array} Data that is needed for initial overlays render and can be consumed by <DrawMap /> component
 */
export const getInitialOverlaysMapFromMissions = (
  missions,
  { isClient, clickable = true },
) =>
  Object.values(missions).reduce(
    (
      acc,
      {
        id,
        boundary,
        boundaryColour,
        householdsAmount,
        density,
        dateTime,
        status,
        label,
      },
    ) => {
      acc[id] = {
        id,
        paths: [
          boundary.map(({ latitude, longitude }) => ({
            lat: latitude,
            lng: longitude,
          })),
        ],
        color: boundaryColour,
        mapPolygonProps: {
          key: id,
          clickable: clickable,
          disabled: isClient,
          canBeDeleted: NON_STARTED_STATUSES.includes(status),
          editingBlocked: NON_EDITABLE_STATUSES.includes(status),
          link: `${PATHS.main.dashboard}/mission/${id}`,
          payload: {
            // When we update a mission it stores the updated date as a local date
            // because the client at this point has no knowledge of the timezone
            // and it shouldn't.
            // But this then raises existential questions such as we shouldn't be
            // storing the ISO vesion of the date in redux state, but to avoid
            // changing to much for this bug fix I am making the code handle both
            // scenarios here
            date: isLocalDateStr(dateTime)
              ? new Date(dateTime)
              : formatToLocalDate(new Date(dropTimeZoneFromDate(dateTime))),
            label,
          },
        },
      }
      return acc
    },
    {},
  )

/**
 * Generates breadcrumbs for the RosterView page that can be consumed by <BreadcrumbsRenderer />
 *
 * @param {object} param.campaign Roster's campaign data
 * @param {number} param.cityPlanId Roster's city plan ID
 * @param {number} param.campaignId Roster's campaign ID
 * @param {boolean} param.fromCityScreen Indicates whether user come to this page from the city screen
 * @param {object} param.city Roster's city
 *
 * @returns {array} Items that can be consumed by <BreadcrumbsRenderer />
 */
export const getBreadcrumbs = ({ campaign, cityPlanId, campaignId, city }) => {
  const campaignsPathname = getCampaignsLink()
  const campaignPathname = getCampaignLink(campaignId)
  const cityPlanPathname = getCityPlanLink(campaignId, cityPlanId)

  return {
    icon: <CampaignsIcon style={{ width: 16 }} />,
    items: [
      {
        title: 'Campaigns',
        pathname: campaignsPathname,
      },
      {
        title: `${campaign.name} City Plans`,
        pathname: campaignPathname,
      },
      {
        title: `${city.name} City Plan`,
        pathname: `${cityPlanPathname}/missions`,
      },
    ],
  }
}
/**
 * Update raw missions with mapAreaData
 * @param {array} param.missions Missions data
 * @param {object} param.missionsAreaDataMap Hash map where key is a mission id and value is a object woth area's data
 */
export const mergeAreaDataToTheMission = ({
  missions,
  missionsAreaDataMap,
  relatedPostcodes = [],
}) =>
  missions.reduce((acc, mission, index) => {
    const mapAreaData = missionsAreaDataMap[mission.id]

    const label = mission.label
      ? mission.label
      : relatedPostcodes && relatedPostcodes[0]

    // If mapAreaData is available for that mission - merge, otherwise - push mission without it
    if (mapAreaData) {
      acc.push({
        ...mission,
        ...mapAreaData,
        label,
      })
    } else {
      acc.push(mission)
    }
    return acc
  }, [])

/**
 * Processes received data and returns new missions (so we won't overfetch when querying PF)
 * and deleted items IDs for further merging
 * @param {array} param.receivedMissions New data
 * @param {array} param.existingMissions Existing data
 */
export const getMissionsUpdateResult = ({
  receivedMissionsMap,
  existingMissionsMap,
}) => {
  const receivedMissionsIds = Object.keys(receivedMissionsMap)
  const existingMissionsIds = Object.keys(existingMissionsMap)
  if (existingMissionsIds.length === 0) {
    return {
      newMissionsMap: receivedMissionsMap,
      deletedMissionsIds: [],
    }
  }

  const isMissionChanged = (mission, prevMissionObj) => {
    return (
      prevMissionObj.dateTime !== mission.dateTime ||
      prevMissionObj.status !== mission.status ||
      prevMissionObj.label !== mission.label ||
      JSON.stringify(prevMissionObj.boundary) !==
        JSON.stringify(mission.boundary)
    )
  }

  return {
    newMissionsMap: Object.values(receivedMissionsMap).reduce(
      (acc, receivedMission) => {
        if (
          !existingMissionsIds.includes(receivedMission.id) ||
          isMissionChanged(
            receivedMission,
            existingMissionsMap[receivedMission.id],
          )
        ) {
          acc[receivedMission.id] = receivedMission
        }
        return acc
      },
      {},
    ),
    deletedMissionsIds: existingMissionsIds.filter(
      existingMissionId => !receivedMissionsIds.includes(existingMissionId),
    ),
  }
}

export const removeDeletedItemsFromArray = (items, deletedIds) =>
  items.filter(({ id }) => !deletedIds.includes(id))

export const removeDeletedItemsFromObject = (object, deletedIds) => {
  return deletedIds.reduce(
    (acc, deletedId) => {
      delete acc[deletedId]
      return acc
    },
    { ...object },
  )
}

/**
 * Merges new overlays with old and removes deleted items
 * @param {array} param.newData Received data
 * @param {array} param.oldData Existing data
 * @param {array} param.deletedIds IDs of the deleted items
 */
export const mergeInitialOverlaysData = ({ newData, oldData, deletedIds }) =>
  removeDeletedItemsFromObject({ ...oldData, ...newData }, deletedIds)

/**
 * Merges new missions with old and removes deleted items
 * @param {array} param.newData Received data
 * @param {array} param.oldData Existing data
 * @param {array} param.deletedIds IDs of the deleted items
 */
export const mergeMissionsData = ({ newData, oldData, deletedIds }) =>
  removeDeletedItemsFromObject({ ...oldData, ...newData }, deletedIds)

/**
 * Merges new checked items map with old and removes deleted items
 * @param {object} param.newData Received data
 * @param {object} param.oldData Existing data
 * @param {array} param.deletedIds IDs of the deleted items
 */
export const mergeVisibleOverlaysData = ({ newData, oldData, deletedIds }) =>
  removeDeletedItemsFromObject({ ...oldData, ...newData }, deletedIds)

/**
 * Filters only non started missions (because impossible to delete started missions)
 * Returns array of missions that are possible to delete and indicator if whether of the received missions were booked
 * @param {array} missions Missions
 * @param {array} missionsToBeDeletedIds IDs of the missions that user tries to delete
 *
 * @returns return.missionsToDelete Missions that could be deleted
 * @returns return.isAnyBooked Indicates if whether of the received missions were booked
 */
export const getMissionsDeletionData = (missions, missionsToBeDeletedIds) =>
  missions.reduce(
    (acc, { id, status }) => {
      // Filter only non started missionns
      if (
        missionsToBeDeletedIds.includes(id) &&
        NON_STARTED_STATUSES.includes(status)
      ) {
        acc.missionsToDelete.push({ id, status })
        // Indicate whether any of the missions are booked so we can display a warning later
        if (status === MISSION_STATUS_ENUM.BOOKED) {
          acc.isAnyBooked = true
        }
      }
      return acc
    },
    { missionsToDelete: [], isAnyBooked: false },
  )



// WEBPACK FOOTER //
// ./src/Containers/LetterboxMissions/util.js